import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMutation, useQuery as useReactQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ConvexError } from "convex/values";
import { Id } from "@cvx/_generated/dataModel";
import { Link } from "lucide-react";
import PlatformIcon from "@/components/Generic/PlatformIcon";
import StatusBadge from "@/components/Generic/StatusBadge";
import { TEAM_USER_IDS } from "@/constants/userIds";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route as VideoProjectRoute } from "@/routes/_app/_auth/partnerships/$partnershipId";
import { api } from "@cvx/_generated/api";
import { calculateEarningsForContract } from "@cvx/helpers";
import { convexQuery } from "@convex-dev/react-query";
import { formatCurrency } from "@/utils/format";
import { formatDate } from "@/utils/format";
import { useAccount } from "@/state/useAccount";
import { useConvexMutation } from "@convex-dev/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { useToast } from "@/components/ui/use-toast";

interface PartnershipPerformanceProps {
  contractId: Id<"creatorContracts">;
}

export function PartnershipPerformance({
  contractId,
}: PartnershipPerformanceProps) {
  const { creatorId } = useAccount();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Get current user to check if they're a team member
  const { data: currentUser } = useReactQuery({
    ...convexQuery(api.app.getCurrentUser, {}),
  });

  // Check if current user is a team member
  const isCreatorGridTeamMember = currentUser
    ? TEAM_USER_IDS.includes(currentUser._id)
    : false;

  const { mutateAsync: linkVideoToPublished } = useMutation({
    mutationFn: useConvexMutation(api.videos.linkVideoProjectToContractVideo),
  });

  const handleLinkToPublishedVideo = async (
    projectId: Id<"creatorVideoProjects">,
    contractVideoId: Id<"contractVideos">,
  ) => {
    try {
      await linkVideoToPublished({
        projectId,
        contractVideoId,
      });

      toast({
        title: "Video linked successfully",
        description: "The project has been linked to the published video",
      });
    } catch (error) {
      console.error("Error linking video:", error);
      toast({
        title: "Error linking video",
        description:
          error instanceof ConvexError
            ? error.data
            : "There was an error linking the video",
        variant: "destructive",
      });
    }
  };

  const partnershipData = useQuery(
    api.partnerships.getPartnershipHistoricalPerformance,
    {
      contractId,
      accountType: creatorId ? "creator" : "brand",
    },
  );

  if (!partnershipData) {
    return (
      <div className="grid gap-6">
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-medium text-primary">Loading...</h2>
          </div>
        </Card>
      </div>
    );
  }

  // Filter videos based on teamVideoReviewRequired before any other calculations
  const visibleVideos = partnershipData.videos.filter((video) => {
    // If teamVideoReviewRequired is true and user is not a team member,
    // only show approved videos
    const campaign = partnershipData.campaign;
    if (
      campaign &&
      campaign.teamVideoReviewRequired &&
      !isCreatorGridTeamMember
    ) {
      return video.status === "approved";
    }
    return true;
  });

  // Calculate earnings breakdown using the helper function
  // Filter out rejected videos only for earnings calculations
  const validVideosForEarnings = visibleVideos.filter(
    (video) => video.status === "approved" || !video.status,
  );

  const { earningsFlatFee, earningsPerVideoBonus, earningsCPM } =
    calculateEarningsForContract(
      partnershipData.contract,
      validVideosForEarnings,
    );

  const totalEarnings = earningsFlatFee + earningsPerVideoBonus + earningsCPM;

  // Sort ALL visible videos by post date (newest first)
  const sortedVideos = visibleVideos.sort(
    (a, b) =>
      new Date(b.postDate || b._creationTime).getTime() -
      new Date(a.postDate || a._creationTime).getTime(),
  );

  // Filter out unpublished videos that already have a contractVideoId
  // Also filter based on teamVideoReviewRequired
  const unpublishedVideos =
    partnershipData.unpublishedVideosWithVersions.filter((video) => {
      // Filter out videos that already have a contractVideoId
      if (video.contractVideoId) {
        return false;
      }

      // If teamVideoReviewRequired is true and user is a founder (not a creator) and not a team member,
      // only show approved video projects
      const campaign = partnershipData.campaign;
      if (
        campaign &&
        campaign.teamVideoReviewRequired &&
        !creatorId && // Only filter for founders, not creators
        !isCreatorGridTeamMember
      ) {
        const latestVersion = video.versions[video.versions.length - 1];
        return latestVersion?.status === "approved";
      }

      return true;
    });

  // For challenge campaigns, only count approved videos in main stats
  // For non-challenge campaigns, count all videos as before
  const isChallengeCampaign = partnershipData.campaign?.type === "challenge";

  // Use sortedVideos which already has the teamVideoReviewRequired filter applied
  const videosForStats = isChallengeCampaign
    ? sortedVideos.filter((video) => video.status === "approved")
    : sortedVideos;

  // Get total number of videos (published + unpublished)
  // Challenge campaigns don't have unpublished videos, so we only count published ones
  const totalVideos = isChallengeCampaign
    ? videosForStats.length
    : videosForStats.length + unpublishedVideos.length;

  return (
    <div className="grid gap-6">
      {/* Earnings Breakdown */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-medium text-primary">
            Contract Performance
          </h2>
          <div className="mt-4 grid grid-cols-3 gap-4">
            {partnershipData?.postsLeft !== null &&
            partnershipData?.postsLeft !== undefined &&
            !isNaN(partnershipData?.postsLeft) &&
            partnershipData?.totalRequiredVideos &&
            !isNaN(partnershipData?.totalRequiredVideos) &&
            partnershipData?.totalRequiredVideos > 0 ? (
              <div className="rounded-lg bg-secondary p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-primary/60">Content Progress</p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-primary">
                      {totalVideos} of {partnershipData.totalRequiredVideos}
                    </span>
                    <span className="text-xs text-primary/60">
                      {Math.round(
                        (totalVideos / partnershipData.totalRequiredVideos) *
                          100,
                      )}
                      %
                    </span>
                  </div>

                  <div className="w-full bg-background rounded-full h-1.5">
                    <div
                      className="bg-primary h-1.5 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min(
                          100,
                          (totalVideos / partnershipData.totalRequiredVideos) *
                            100,
                        )}%`,
                      }}
                    />
                  </div>

                  <div className="text-xs text-primary/60">
                    {partnershipData.postsLeft} posts left
                  </div>
                  <div className="text-xs text-primary/60">
                    {sortedVideos.length} published · {unpublishedVideos.length}{" "}
                    in progress
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg bg-secondary p-4">
                <p className="text-sm text-primary/60">Total Videos</p>
                <p className="text-2xl font-medium text-primary">
                  {totalVideos}
                </p>
                <p className="text-xs text-primary/60 mt-1">
                  {sortedVideos.length} published · {unpublishedVideos.length}{" "}
                  in progress
                </p>
              </div>
            )}
            <div className="rounded-lg bg-secondary p-4">
              <p className="text-sm text-primary/60">Total Views</p>
              <p className="text-2xl font-medium text-primary">
                {videosForStats
                  .reduce((sum, video) => sum + (video.views || 0), 0)
                  .toLocaleString()}
              </p>
            </div>
            <div className="rounded-lg bg-secondary p-4">
              <p className="text-sm text-primary/60">Total Earnings</p>
              <p className="text-2xl font-medium text-primary">
                {formatCurrency(totalEarnings)}
              </p>
            </div>
          </div>

          {/* Earnings Breakdown */}
          {(earningsFlatFee > 0 ||
            earningsPerVideoBonus > 0 ||
            earningsCPM > 0) && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-primary mb-3">
                Earnings Breakdown
              </h3>
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: `repeat(${
                    [
                      earningsFlatFee > 0,
                      earningsPerVideoBonus > 0,
                      earningsCPM > 0,
                    ].filter(Boolean).length
                  }, minmax(0, 1fr))`,
                }}
              >
                {earningsFlatFee > 0 && (
                  <div className="rounded-lg border border-border bg-card p-4">
                    <p className="text-sm text-primary/60">Base Payment Fees</p>
                    <p className="text-lg font-medium text-primary">
                      {formatCurrency(earningsFlatFee)}
                    </p>
                  </div>
                )}
                {earningsPerVideoBonus > 0 && (
                  <div className="rounded-lg border border-border bg-card p-4">
                    <p className="text-sm text-primary/60">Per Video Fees</p>
                    <p className="text-lg font-medium text-primary">
                      {formatCurrency(earningsPerVideoBonus)}
                    </p>
                  </div>
                )}
                {earningsCPM > 0 && (
                  <div className="rounded-lg border border-border bg-card p-4">
                    <p className="text-sm text-primary/60">CPM Earnings</p>
                    <p className="text-lg font-medium text-primary">
                      {formatCurrency(earningsCPM)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Published Videos Table */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-medium text-primary">Published Videos</h2>

          {sortedVideos.length === 0 ? (
            <div className="mt-4 text-center py-8">
              <p className="text-primary/60">
                No published videos found for this contract
              </p>
            </div>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <TooltipProvider>
                <table className="w-full">
                  <thead className="border-b border-border bg-secondary">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-primary/60">
                        Title
                      </th>
                      <th className="w-16 px-4 py-3 text-center text-sm font-medium text-primary/60">
                        Platform
                      </th>
                      {partnershipData.campaign?.type === "challenge" && (
                        <th className="px-4 py-3 text-center text-sm font-medium text-primary/60">
                          Status
                        </th>
                      )}
                      <th className="px-4 py-3 text-right text-sm font-medium text-primary/60">
                        Views
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-primary/60">
                        Likes
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-primary/60">
                        Comments
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-primary/60">
                        Earnings
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-primary/60">
                        Date Posted
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-primary/60">
                        Last Updated
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {sortedVideos.map((video) => (
                      <tr key={video._id} className="hover:bg-secondary/50">
                        <td className="px-4 py-4">
                          <a
                            href={video.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline"
                            title={video.title || "Untitled Video"}
                          >
                            {video.title || "Untitled Video"}
                          </a>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex justify-center">
                            <PlatformIcon platform={video.platform ?? ""} />
                          </div>
                        </td>
                        {partnershipData.campaign?.type === "challenge" && (
                          <td className="px-4 py-4">
                            <div className="flex justify-center">
                              <StatusBadge
                                status={video.status || "pending"}
                                clickable={true}
                                video={video}
                                campaignType={partnershipData.campaign?.type}
                              />
                            </div>
                          </td>
                        )}
                        <td className="px-4 py-4 text-right text-sm text-primary">
                          {(video.views || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-4 text-right text-sm text-primary">
                          {(video.likes || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-4 text-right text-sm text-primary">
                          {(video.comments || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-4 text-right text-sm font-medium text-primary">
                          <span
                            className={
                              video.status === "rejected"
                                ? "line-through opacity-60"
                                : ""
                            }
                          >
                            {formatCurrency(video.earnings || 0)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right text-sm text-primary">
                          {video.postDate
                            ? formatDate(new Date(video.postDate).toISOString())
                            : ""}
                        </td>
                        <td className="px-4 py-4 text-right text-sm text-primary">
                          {video.lastUpdated
                            ? formatDate(
                                new Date(video.lastUpdated).toISOString(),
                              )
                            : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TooltipProvider>
            </div>
          )}
        </div>
      </Card>

      {/* Unpublished Videos Table */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-medium text-primary">
            Unpublished Videos
          </h2>

          {unpublishedVideos.length === 0 ? (
            <div className="mt-4 text-center py-8">
              <p className="text-primary/60">
                No unpublished videos found for this contract
              </p>
            </div>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border bg-secondary">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-primary/60">
                      Title
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-primary/60">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-primary/60">
                      Versions
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-primary/60">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {unpublishedVideos.map((video) => (
                    <tr
                      key={video._id}
                      className="hover:bg-secondary/50 cursor-pointer"
                      onClick={(e) => {
                        // Prevent navigation when clicking the dropdown
                        if (
                          (e.target as HTMLElement).closest(
                            ".dropdown-container",
                          )
                        ) {
                          e.stopPropagation();
                          return;
                        }
                        navigate({
                          to: VideoProjectRoute.fullPath,
                          params: {
                            partnershipId: contractId,
                          },
                          search: {
                            tab: "videos",
                            projectId: video._id,
                            versionId:
                              video.versions[video.versions.length - 1]?._id,
                          },
                        });
                      }}
                    >
                      <td className="px-4 py-4">
                        <span className="text-sm text-primary">
                          {video.title || "Untitled Video"}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right text-sm text-primary">
                        {video.versions[video.versions.length - 1]?.status ||
                          "Draft"}
                      </td>
                      <td className="px-4 py-4 text-right text-sm text-primary">
                        {video.versions?.length || 0}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div
                          className="dropdown-container"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-[110px]"
                                disabled={
                                  video.versions[video.versions.length - 1]
                                    ?.status !== "approved"
                                }
                              >
                                <Link className="h-4 w-4 mr-2" />
                                Link Video
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="w-[300px]"
                            >
                              <DropdownMenuLabel>
                                Link to Published Video
                              </DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {sortedVideos.length > 0 ? (
                                sortedVideos
                                  .filter(
                                    (publishedVideo) =>
                                      // Check if the video isn't already linked to any unpublished video project
                                      !partnershipData.unpublishedVideosWithVersions.some(
                                        (unpublishedVideo) =>
                                          unpublishedVideo.contractVideoId ===
                                          publishedVideo._id,
                                      ),
                                  )
                                  .map((publishedVideo) => (
                                    <DropdownMenuItem
                                      key={publishedVideo._id}
                                      onClick={() =>
                                        handleLinkToPublishedVideo(
                                          video._id,
                                          publishedVideo._id,
                                        )
                                      }
                                    >
                                      <div className="flex items-center w-full">
                                        <div className="mr-2">
                                          <PlatformIcon
                                            platform={
                                              publishedVideo.platform ?? ""
                                            }
                                          />
                                        </div>
                                        <div className="flex-1">
                                          <div className="font-medium truncate">
                                            {publishedVideo.title ||
                                              "Untitled Video"}
                                          </div>
                                          <div className="text-xs text-primary/60">
                                            {publishedVideo.views?.toLocaleString() ||
                                              0}{" "}
                                            views
                                          </div>
                                        </div>
                                      </div>
                                    </DropdownMenuItem>
                                  ))
                              ) : (
                                <DropdownMenuItem disabled>
                                  No published videos available
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
